import {
  FilterQuery,
  FilterQueryRequest
} from 'src/app/models/filter-query.model';
import { GeoJsonTypesEnum } from 'src/app/enums/geojson-types.enum';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  tap,
  catchError,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  switchMap,
  skip,
  Subscription,
  takeUntil,
  take,
  toArray
} from 'rxjs';
import { materializedRiskColumn } from 'src/app/constants/risk-column-alias.const';
import { loadSelectedRows } from 'src/app/services/data-service/load-selected-rows';
import {
  isAlwaysVisibleColumn,
  isDefaultVisibleNewColumn,
  PLANTATION_LIST_TABLE_COLUMNS,
  SUPPLIER_NAME_COLUMN_FIELD
} from 'src/app/constants/table-columns.const';
import { AGRITRACE_PREVIEW_STATUS } from 'src/app/enums/agritrace-preview-status.enum';
import { ScreenEnum } from 'src/app/enums/screens.enum';
import {
  AGD_GEOSPATIAL_COMPUTE_SOURCE,
  Company,
  CrdState,
  Dataset,
  GeometryData,
  INITIAL_CRD_STATE,
  INITIAL_PAGED_STATE,
  INITIAL_SELECTED_PERIOD,
  Plantation,
  PlantationTableColumnsSettings,
  RiskLayer,
  RiskOverlapDetail,
  RiskProperty,
  SiDetailsQueryParams,
  TileUrlResult,
  LoadedTotalSummary,
  SummarySection
} from 'src/app/models/crd-state.interface';
import { GeometryPayload } from 'src/app/models/geometry-payload.model';
import { PagedResponse } from 'src/app/models/paged-response.model';
import { ParsedUploadPlantation } from 'src/app/models/parsed-upload-plantation.model';
import { PreviewUrlPayload } from 'src/app/models/risk-mitigation-v2.model';
import { Settings, SettingsPayload } from 'src/app/models/settings.model';
import {
  RiskValueTableParams,
  TableParams
} from 'src/app/models/table-params.model';
import {
  DashboardService,
  SummaryQueryParams
} from '../data-service/dashboard.service';
import {
  LOCALLY_HANDLED,
  reportUnexpectedError
} from 'src/app/utils/http-error.util';
import { HttpContext } from '@angular/common/http';
import { MapPageService } from '../data-service/map-page.service';
import { RiskMitigationService } from '../data-service/risk-mitigation.service';
import { SettingsService } from '../data-service/settings.service';
import { EventStateService } from './event-state.service';
import { EvidenceStateService } from './evidence-state.service';
import { PlotReviewStateService } from './plot-review-state.service';
import { StateService } from './state.service';
import { UserStateService } from './user-state.service';
import { TablePersistence } from 'src/app/services/table-persistence';
import { STORAGE_KEYS } from 'src/app/constants/storage-keys.const';
import { PosthogService } from '../posthog.service';
import { POSTHOG_KEYS } from 'src/app/constants/posthog-keys.const';

@Injectable({
  providedIn: 'root'
})
export class CrdStateService extends StateService<CrdState> {
  private readonly userState = inject(UserStateService);
  private readonly posthogService = inject(PosthogService);
  private readonly errorHandler = inject(ErrorHandler);
  readonly selectionState = new TablePersistence(
    STORAGE_KEYS.PLANTATION_TABLE_STATE
  );
  private linkingMode: string | null = null;

  // used in interceptor
  private _crd = '';
  private _plantationTable = '';
  private readonly datasetContextSubject = new BehaviorSubject({
    crd: '',
    plantationTable: ''
  });
  readonly datasetContext$ = this.datasetContextSubject.asObservable();
  private riskPropertiesGeneration = 0;
  private plantationsSubscription?: Subscription;
  private questionnairesSubscription?: Subscription;
  private summarySubscription?: Subscription;
  // The last summary request, so one card can retry with the same filters.
  private summaryParams?: SummaryQueryParams;
  private summaryGeneration = 0;
  private filtersSubscription?: Subscription;

  private invalidateDatasetMetadata() {
    this.resetRiskProperties();
    this.setState({ plantationListFilters: undefined });
    this.datasetContextSubject.next({
      crd: this.crd,
      plantationTable: this.plantationTable
    });
  }
  private siPlantationListSubject = new BehaviorSubject<any[]>([]);
  siPlantationList$ = this.siPlantationListSubject.asObservable();

  get crd() {
    return this._crd;
  }

  set crd(crd: string) {
    if (this._crd === crd) return;
    this._crd = crd;
    this.invalidateDatasetMetadata();
  }

  get plantationTable() {
    return this._plantationTable;
  }

  set plantationTable(plantationTable: string) {
    if (this._plantationTable === plantationTable) return;
    this._plantationTable = plantationTable;
    this.invalidateDatasetMetadata();
  }

  selectedPlantationList$ = this.select(
    (state) => state.selectedPlantationList
  );
  prevPlantationList$ = this.select((state) => state.prevPlantationList);
  linkedPlantationList$ = this.select((state) => state.linkedPlantationList);
  pagedPlantationList$ = this.select((state) => state.pagedPlantationList);

  pagedQuestionnaireList$ = this.select(
    (state) => state.pagedQuestionnaireList
  );

  selectedQuestionnaireList$ = this.select(
    (state) => state.selectedQuestionnaireList
  );

  pagedRiskValues$ = this.select((state) => state.pagedRiskValues);
  pagedRiskValuesModal$ = this.select((state) => state.pagedRiskValuesModal);
  plantationListFilters$ = this.select((state) => state.plantationListFilters);
  questionnaireListFilters$ = this.select(
    (state) => state.questionnaireListFilters
  );
  riskOverlapDetails$ = this.select((state) => state.riskOverlapDetails);
  riskLayers$ = this.select((state) => state.riskLayers);
  geometryData$ = this.select((state) => state.geometryData);
  selfOverlapGeometryData$ = this.select(
    (state) => state.selfOverlapGeometryData
  );

  checkedSelectedPlantationList$ = combineLatest([
    this.selectedPlantationList$,
    this.geometryData$
  ]).pipe(
    map(([plantationList, geometryData]) => {
      if (!plantationList?.length || !geometryData?.length) {
        return [] as Plantation[];
      }
      const geoByCode = new Map(
        geometryData.map((geo) => [geo.plantation_code, geo._geo] as const)
      );
      return plantationList.map((plantation) => ({
        ...plantation,
        mapData: geoByCode.get(plantation.plantation_code)
      })) as Plantation[];
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  mapStyle$ = this.select((state) => state.mapStyle);
  currentScreen$ = this.select((state) => state.currentScreen);
  geometryDataExport$ = this.select((state) => state.geometryDataExport);
  datasets$ = this.select((state) => state.datasets);
  selectedDataset$ = this.select((state) => state.selectedDataset);
  selectedPeriod$ = this.select((state) => state.selectedPeriod);
  parsedFileData$ = this.select((state) => state.parsedFileData);
  parsedFileErrors$ = this.select((state) => state.parsedFileErrors);
  uploadFileMappingColumns$ = this.select(
    (state) => state.uploadFileMappingColumns
  );
  totalSummary$ = this.select((state) => state.totalSummary);
  riskPropertiesError$ = this.select((state) => !!state.riskPropertiesError);
  // The list waits on the risk configuration, so either failure blocks it.
  plantationListError$ = this.select(
    (state) => !!state.plantationListError || !!state.riskPropertiesError
  );
  riskValueAggregates$ = this.select((state) => state.riskValueAggregates);
  riskProperties$ = this.select((state) => state.riskProperties);
  favoritePlantationFilters$ = this.select(
    (state) => state.favoritePlantationFilters
  );
  siDetails$ = this.select((state) => state.siDetails);
  settings$ = this.select((state) => state.settings);
  plantationTableColumnSettings$ = this.select(
    (state) => state.plantationTableColumnSettings
  );
  savedSettings$ = this.select((state) => state.savedSettings);
  countries$ = this.select((state) => state.countries);
  bulkSearch$ = this.select((state) => state.bulkSearch);
  riskDataByPlantationCode$ = this.select(
    (state) => state.riskDataByPlantationCode
  );
  agriTraceFiles$ = this.select((state) => state.agriTraceFiles);
  agriTraceFilePreview$ = this.select((state) => state.agriTraceFilePreview);
  allPlantations$ = this.select((state) => state.allPlantations);

  constructor(
    private dashboardService: DashboardService,
    private mapPageService: MapPageService,
    private riskMitigationService: RiskMitigationService,
    private eventStateService: EventStateService,
    private settingsService: SettingsService,
    private translocoService: TranslocoService,
    private plotReviewStateService: PlotReviewStateService,
    private evidenceStateService: EvidenceStateService
  ) {
    super(INITIAL_CRD_STATE);
  }

  setSelectedPlantationList(plantations: Plantation[], persist = true) {
    if (persist)
      this.selectionState.saveSelection(
        this.getSelectionScope(),
        plantations.map((row) => row.plantation_code)
      );
    const selectedPlantations = [
      ...plantations.map((plantation) => ({ ...plantation, checked: true }))
    ];
    this.setState({ selectedPlantationList: selectedPlantations });
  }

  setPrevPlantationList(plantations: Plantation[]) {
    const prevPlantations = [
      ...plantations.map((plantation) => ({ ...plantation, checked: true }))
    ];
    this.setState({ prevPlantationList: prevPlantations });
  }

  setLinkedPlantationList(plantations: Plantation[]) {
    const linkedPlantations = [
      ...plantations.map((plantation) => ({ ...plantation, checked: true }))
    ];
    this.setState({ linkedPlantationList: linkedPlantations });
  }

  setSelectedQuestionnaireList(plantations: Plantation[]) {
    const selectedQuestionnaires = [
      ...plantations.map((plantation) => ({ ...plantation, checked: true }))
    ];
    this.setState({ selectedQuestionnaireList: selectedQuestionnaires });
  }

  setRiskLayers(riskLayers: RiskLayer[]) {
    this.setState({ riskLayers: riskLayers });
  }

  setGeometryData(geometryData: GeometryData[]) {
    this.setState({ geometryData: geometryData });
  }

  setRiskOverlapDetails(riskOverlapDetails: RiskOverlapDetail[]) {
    this.setState({ riskOverlapDetails: riskOverlapDetails });
  }

  setBulkSearch(bulkSearch: { key: string; value: string[] } | undefined) {
    this.setState({ bulkSearch });
  }

  setMapStyle(mapStyle: string) {
    this.setState({ mapStyle: mapStyle });
  }

  // TODO: remove conditional rednering related code
  setCurrentScreen(screen: ScreenEnum) {
    this.setState({ currentScreen: screen });
  }

  setSelectedDataset(dataset: Dataset | null) {
    const crd = dataset?.name ?? '';
    const plantationTable = dataset?.default_plantations_table ?? '';
    if (this.crd !== crd || this.plantationTable !== plantationTable) {
      this._crd = crd;
      this._plantationTable = plantationTable;
      this.invalidateDatasetMetadata();
    }
    this.setState({ selectedDataset: dataset });
  }

  setSelectedPeriod(period: string) {
    this.setState({ selectedPeriod: period });
  }

  setSiDetails(siDetails: SiDetailsQueryParams) {
    const company: Company & { linkingMode: string } = JSON.parse(
      siDetails.company
    );
    this.setState({
      siDetails: {
        ...siDetails,
        company: {
          type:
            company.type === 'Consumer'
              ? 'DASHBOARD.SI_LINKING.CONSUMER'
              : 'DASHBOARD.SI_LINKING.PRODUCER',
          name: company.name
        },
        siLinkingMode: company.linkingMode.toLowerCase()
      }
    });
  }

  setSiPlantations(plantations: any[]) {
    this.siPlantationListSubject.next(plantations);
  }

  // TODO: update type
  setParsedFileData(data: ParsedUploadPlantation[]) {
    this.setState({ parsedFileData: data });
  }

  resetDashboardStates() {
    this.setState({
      pagedPlantationList: INITIAL_PAGED_STATE,
      plantationListError: false
    });
    this.setState({ favoritePlantationFilters: undefined });
    this.setState({ plantationListFilters: undefined });
    this.setState({ totalSummary: undefined });
    this.setState({ plantationTableColumnSettings: undefined });
    this.eventStateService.isTotalSummaryLoading = true;
  }

  resetRiskProperties() {
    this.riskPropertiesGeneration++;
    this.setState({ riskProperties: undefined, riskPropertiesError: false });
  }

  resetUploadFileMappingColumns() {
    this.setState({ uploadFileMappingColumns: undefined });
  }

  resetParsedFileErrors() {
    this.setState({ parsedFileErrors: undefined });
  }

  getGeometryDataExport(plantationCodes: string[]) {
    this.dashboardService
      .getGeometryDataForExport(plantationCodes)
      .subscribe((res) => this.setState({ geometryDataExport: res }));
  }

  getGeometryData(params: GeometryPayload, isSiLinking = false) {
    this.mapPageService
      .getGeometryData(params, isSiLinking)
      .subscribe((res) => {
        this.setState({ geometryData: res });
      });
  }

  getDatasets() {
    this.dashboardService
      .getDatasets()
      .subscribe((res) => this.setState({ datasets: res }));
  }

  // AGD-152: source risk IDs from the dataset's configured risk properties
  // (returned by `GET /v3/datasets/risks/?crd=...&plantation_table=...` via
  // DashboardService.getRiskProperties — populated into state.riskProperties$
  // by CrdShellComponent). Each emission fetches tile URLs from the crd-api
  // proxy and publishes the resulting layer set as state.riskLayers.
  // Returns the Subscription so callers can manage its lifetime alongside the
  // map view.
  initTileUrlsRefresh() {
    return this.riskProperties$
      .pipe(
        filter(
          (riskProperties): riskProperties is RiskProperty[] =>
            !!riskProperties?.length
        ),
        map((riskProperties) => ({
          riskIds: riskProperties.map((risk) => this.getTileRiskId(risk)),
          riskProperties
        })),
        distinctUntilChanged(
          (prev, curr) =>
            prev.riskIds.length === curr.riskIds.length &&
            prev.riskIds.every((id, i) => id === curr.riskIds[i])
        ),
        switchMap(({ riskIds, riskProperties }) =>
          this.mapPageService
            .getTileUrls(riskIds, this.getTileUrlsCacheScope())
            .pipe(
              map((response) =>
                this.toRiskLayers(response.results || [], riskProperties)
              ),
              catchError((error: unknown) => {
                // eslint-disable-next-line no-console
                console.error('[AGD-152] tile-urls error:', error);
                return of<RiskLayer[]>([]);
              })
            )
        )
      )
      .subscribe((riskLayers) => this.setState({ riskLayers: riskLayers }));
  }

  private getTileUrlsCacheScope() {
    return `${this.crd}:${this.plantationTable}`;
  }

  /**
   * Resolves the identifier `/v2/layers/tile-urls` should be asked for, and
   * that the response is then correlated back by.
   *
   * Geospatial catalog entries keep a frozen storage `name` while their
   * compute version moves on, so for those the tile must be addressed by
   * `risk_function` — requesting the `name` draws the superseded raster under
   * the current label. The gate matters: `bq-udfs` rows also carry a
   * `risk_function`, but it is a BigQuery UDF name the tile service cannot
   * dispatch, so sending it would drop those layers entirely. Everything else
   * addresses tiles by `name`.
   */
  private getTileRiskId(risk: RiskProperty): string {
    return risk.compute_source === AGD_GEOSPATIAL_COMPUTE_SOURCE
      ? risk.risk_function?.trim() || risk.name
      : risk.name;
  }

  // AGD-152: map the tile-urls proxy response into RiskLayer[] suitable for
  // Mapbox raster sources + the layers toggle UI. Drops entries that failed
  // upstream (no xyz_url / error set) and looks up display_name from
  // riskProperties so the panel shows a human-readable label.
  //
  // Results are correlated by getTileRiskId() — the same identifier the
  // request was built from — so the label and colour stay attached to the
  // raster that was actually asked for. `id` remains the returned risk_id; it
  // is only the Mapbox source/layer identity.
  toRiskLayers(
    results: TileUrlResult[],
    riskProperties: RiskProperty[]
  ): RiskLayer[] {
    return results
      .filter((result) => !!result.xyz_url && !result.error)
      .map((result) => {
        const property = riskProperties.find(
          (prop) => this.getTileRiskId(prop) === result.risk_id
        );
        return {
          id: result.risk_id,
          name: property?.display_name ?? result.risk_id,
          dataurl: result.xyz_url as string,
          color: result.color ?? property?.color ?? '#000000',
          type: 'raster',
          checked: false,
          opacity: result.opacity
        };
      });
  }

  getMapStyle() {
    this.mapPageService
      .getMapStyle()
      .subscribe((mapStyle) => this.setState({ mapStyle: mapStyle }));
  }

  getUploadFileMappingColumns() {
    this.dashboardService
      .getUploadMappingColumns()
      .subscribe((columns) =>
        this.setState({ uploadFileMappingColumns: columns })
      );
  }

  getParsedFileErrors(data: ParsedUploadPlantation[]) {
    this.dashboardService
      .validatePlantationData(this.crd, data)
      .pipe(take(1))
      .subscribe((errors) => {
        this.setState({ parsedFileErrors: errors });
      });
  }

  private getSelectionScope(): string | null {
    const user = this.userState.currentUserKey;
    const { crd: dataset, plantationTable: table } = this;
    const { selectedPeriod: period, siDetails } = this.currentState;
    if (!user || !dataset || !table || !period) return null;

    const scope = {
      user,
      company: this.userState.currentCompanyId,
      dataset,
      table,
      period
    };
    if (this.linkingMode === null) return JSON.stringify(scope);
    if (!siDetails?.siDetailId) return null;

    return JSON.stringify({
      ...scope,
      si: { id: siDetails.siDetailId, mode: this.linkingMode }
    });
  }

  restoreSelection(isSiLinking = false, linkingMode = '') {
    this.linkingMode = isSiLinking ? linkingMode : null;
    const context$ = combineLatest([
      this.select((state) => state),
      this.userState.userInfo$
    ]).pipe(map(() => this.getSelectionScope()));
    return this.selectionState
      .restoreSelection(context$, (ids) => this.loadSelectedPlantations(ids))
      .pipe(tap((rows) => this.setSelectedPlantationList(rows, false)));
  }

  loadSelectedPlantations(ids: string[]) {
    const context: TableParams = {
      crd: this.crd,
      plantation_table: this.plantationTable,
      period: this.getValue('selectedPeriod') as string | undefined,
      pageSize: 500,
      page: 1
    };
    return this.riskProperties$.pipe(
      filter((risks) => risks !== undefined),
      take(1),
      switchMap((risks) =>
        loadSelectedRows(ids, (codes) =>
          this.fetchAndEnrichPlantations(
            {
              ...context,
              filters: { AND: { plantation_code: codes }, OR: {} }
            },
            false,
            context.period,
            risks
          )
        )
      )
    );
  }

  getAllPlantations(
    totalCount: number,
    filters: FilterQuery | null | undefined
  ) {
    this.eventStateService.isFetchingAllPlantations = true;

    const pageSize = 1000;
    const totalPages = Math.ceil(totalCount / pageSize);
    const concurrencyLimit = 5;

    return combineLatest([this.riskProperties$, this.selectedPeriod$])
      .pipe(
        filter(
          ([riskProperties, selectedPeriod]) =>
            !!riskProperties && !!selectedPeriod
        ),
        take(1),
        concatMap(([riskProperties, selectedPeriod]) => {
          const pageRequests = Array.from({ length: totalPages }, (_, i) => {
            const page = i + 1;
            const params: TableParams = { page, pageSize, filters };

            return {
              index: i,
              request$: this.fetchAndEnrichPlantations(
                params,
                false,
                selectedPeriod,
                riskProperties
              )
            };
          });

          return from(pageRequests).pipe(
            mergeMap(
              ({ index, request$ }) =>
                request$.pipe(map((result) => ({ index, result }))),
              concurrencyLimit
            ),
            toArray(),
            map((indexedResults) =>
              indexedResults
                .sort((a, b) => a.index - b.index)
                .flatMap((entry) => entry.result)
            )
          );
        })
      )
      .subscribe({
        next: (allPlantations) => {
          this.eventStateService.isFetchingAllPlantations = false;
          this.setState({ allPlantations });
        },
        error: () => {
          this.eventStateService.isFetchingAllPlantations = false;
        }
      });
  }

  clearAllPlantations() {
    this.setState({ allPlantations: [] });
  }

  getPlantationsPaginated(params: TableParams) {
    this.plantationsSubscription?.unsubscribe();
    this.plantationsSubscription = combineLatest([
      this.riskProperties$,
      this.selectedPeriod$
    ])
      .pipe(
        filter(
          ([riskProperties, selectedPeriod]) =>
            !!riskProperties && !!selectedPeriod
        ),
        take(1),
        concatMap(([riskProperties, selectedPeriod]) => {
          if (!riskProperties) return of(null);

          // The table shows its own inline error for a failed page.
          return this.fetchAndEnrichPlantations(
            params,
            true,
            selectedPeriod,
            riskProperties,
            LOCALLY_HANDLED.context
          );
        }),
        takeUntil(this.datasetContext$.pipe(skip(1)))
      )
      .subscribe({
        next: (plantations) => {
          if (plantations) {
            this.setState({
              pagedPlantationList: plantations,
              plantationListError: false
            });
            this.plotReviewStateService.loadForPlantations(plantations.results);
            this.evidenceStateService.loadForPlantations(
              this.crd,
              plantations.results
            );
          }
        },
        error: (err: unknown) => {
          this.setState({
            pagedPlantationList: INITIAL_PAGED_STATE,
            plantationListError: true
          });
          reportUnexpectedError(this.errorHandler, err);
        }
      });
  }

  getQuestionnairesPaginated(params: TableParams) {
    this.questionnairesSubscription?.unsubscribe();
    this.questionnairesSubscription = combineLatest([
      this.riskProperties$,
      this.selectedPeriod$
    ])
      .pipe(
        filter(
          ([riskProperties, selectedPeriod]) =>
            !!riskProperties && !!selectedPeriod
        ),
        take(1),
        concatMap(([riskProperties, selectedPeriod]) => {
          if (!riskProperties) return of(null);
          return this.dashboardService
            .getQuestionnairesPaginated({
              tableParams: { ...params, period: selectedPeriod },
              riskProperties: riskProperties
            })
            .pipe(
              map((plantations) => {
                plantations.results.forEach((plantation: any) => {
                  riskProperties.forEach((prop) => {
                    const riskBoolean = `${prop.name}_value`;
                    if (plantation[riskBoolean]) {
                      plantation.risks?.push(prop);
                    }
                  });
                });

                return plantations;
              })
            );
        }),
        takeUntil(this.datasetContext$.pipe(skip(1)))
      )
      .subscribe((res) => {
        if (res) {
          this.setState({ pagedQuestionnaireList: res });
        }
      });
  }

  getPlantationListFilters() {
    this.filtersSubscription?.unsubscribe();
    this.filtersSubscription = combineLatest([
      this.selectedPeriod$,
      this.datasetContext$
    ])
      .pipe(
        switchMap(([selectedPeriod]) => {
          if (!selectedPeriod) return of([null, null]);
          return combineLatest([
            this.dashboardService.getPlantationListFilters({
              date: selectedPeriod
            }),
            this.riskProperties$
          ]);
        })
      )
      .subscribe(([filters, riskProperties]) => {
        if (filters && riskProperties) {
          const selfOverlap = riskProperties.find(
            (risk) => risk.name === 'risk_self_overlap_v1'
          );

          const otherRisks = riskProperties
            .filter((risk) => risk.name !== selfOverlap?.name)
            .map((risk) => ({
              label: risk.display_name,
              value: { [`${materializedRiskColumn(risk.name)}_value`]: true },
              iconColor: risk.color
            }));

          const noRisks = {
            label: this.translocoService.translate('RISK_TYPES.NO_RISKS'),
            value: { has_any_risk_value: false },
            iconColor: '',
            showDividerBefore: true
          };

          const overlapPair = selfOverlap
            ? [
                {
                  label: selfOverlap.display_name,
                  value: { [`${selfOverlap.name}_value`]: true },
                  iconColor: selfOverlap.color
                },
                {
                  label: this.translocoService.translate(
                    'RISK_TYPES.NO_PLANTATION_OVERLAPS'
                  ),
                  value: { [`${selfOverlap.name}_value`]: false },
                  iconColor: ''
                }
              ]
            : [];

          const groupedRisks = [...overlapPair, noRisks, ...otherRisks];

          const filtersWithRisks = { risks: groupedRisks };

          this.setState({
            plantationListFilters: { ...filters, ...filtersWithRisks }
          });
        }
      });
  }

  getTotalSummary(params?: FilterQueryRequest) {
    this.summarySubscription?.unsubscribe();
    const generation = ++this.summaryGeneration;
    this.summarySubscription = combineLatest([
      this.riskProperties$,
      this.selectedPeriod$
    ])
      .pipe(
        filter(
          (values): values is [RiskProperty[], string] =>
            !!values[0] && !!values[1]
        ),
        take(1),
        concatMap(([riskProperties, selectedPeriod]) => {
          if (!selectedPeriod || !params) return of(null);
          this.summaryParams = {
            ...params,
            period: selectedPeriod,
            riskProperties: riskProperties
          };
          return this.dashboardService.getTotalSummary(this.summaryParams);
        }),
        takeUntil(this.datasetContext$.pipe(skip(1)))
      )
      .subscribe((summary) => {
        if (!summary || generation !== this.summaryGeneration) return;
        const [
          totalLandAreas,
          totalPlots,
          totalMappingEfforts,
          overallRisks,
          mappingDataQuality,
          geospatialRisksSummary
        ] = summary;
        const totals: LoadedTotalSummary = {
          totalPlots,
          totalLandAreas,
          totalMappingEfforts,
          overallRisks,
          mappingDataQuality,
          geospatialRisksSummary
        };

        this.setState({ totalSummary: totals });
      });
  }

  /** Re-runs one card's request with the filters the summary last used. */
  retrySummarySection(section: SummarySection): Observable<unknown> {
    const params = this.summaryParams;
    if (!params) return of(null);
    const generation = this.summaryGeneration;
    return this.dashboardService.getSummarySection(section, params).pipe(
      tap((value) => {
        const current = this.getValue('totalSummary');
        if (!current || generation !== this.summaryGeneration) return;
        this.setState({ totalSummary: { ...current, [section]: value } });
      }),
      takeUntil(this.datasetContext$.pipe(skip(1)))
    );
  }

  // Does not set state - need for invidual risk overlaps
  getPagedRiskValues(params: RiskValueTableParams) {
    // Maps geometry data for hover and zoom plantation implementation
    return this.geometryData$.pipe(
      concatMap((geometryData) =>
        this.mapPageService.getPagedRiskValues(params).pipe(
          map((pagedRiskValues) => {
            const geoMap = new Map(
              geometryData.map((data) => [data.plantation_code, data._geo])
            );
            pagedRiskValues.results.forEach((value) => {
              value.mapData = geoMap.get(value.plantation_code);
            });

            return pagedRiskValues;
          })
        )
      )
    );
  }

  // Does not set state - need for invidual risk overlaps
  getRiskValueAggregates(params: {
    plantationCodes: string[];
    riskType: string;
  }) {
    return this.mapPageService.getRiskValueAggregates({
      plantationCodes: params.plantationCodes,
      riskType: params.riskType
    });
  }

  getRiskProperties() {
    this.resetRiskProperties();
    const generation = this.riskPropertiesGeneration;
    this.dashboardService
      .getRiskProperties(LOCALLY_HANDLED.context)
      // A failed load is not an authoritative empty configuration. The
      // summary cards and the plantation list wait on it, so they show it.
      .pipe(
        catchError((error: unknown) => {
          reportUnexpectedError(this.errorHandler, error);
          return of(undefined);
        })
      )
      .subscribe((res) => {
        if (generation === this.riskPropertiesGeneration) {
          this.setState({ riskProperties: res, riskPropertiesError: !res });
        }
      });
  }

  getFavoritePlantationFilters() {
    this.dashboardService
      .getFavoritePlantationFilters()
      .subscribe((res) => this.setState({ favoritePlantationFilters: res }));
  }

  getSettings() {
    this.settingsService
      .getSettings(this.crd)
      .pipe(take(1))
      .subscribe({
        next: (settingsMap) => {
          const datasetId = settingsMap.values().next().value?.dataset_id || 0;
          const allColumnFields = PLANTATION_LIST_TABLE_COLUMNS.filter(
            (col) => !isAlwaysVisibleColumn(col.field)
          ).map((col) => col.field);

          if (settingsMap.has('plantation_table_columns')) {
            const existingSettings = settingsMap.get(
              'plantation_table_columns'
            )!;
            const columnSettings: PlantationTableColumnsSettings =
              existingSettings.settings_values;
            const existingColumns = Object.keys(columnSettings);

            for (const field of allColumnFields) {
              if (!existingColumns.includes(field)) {
                // Newly-introduced columns default to hidden for existing
                // datasets, except the few that should appear by default
                // (still togglable, so users can explicitly hide them).
                columnSettings[field] = isDefaultVisibleNewColumn(field);
              }
            }
          } else {
            const plantationTableColumns: Settings = {
              dataset_id: datasetId,
              created_at: new Date().toISOString(),
              last_modified_at: new Date().toISOString(),
              settings_key: 'plantation_table_columns',
              settings_values: Object.fromEntries(
                allColumnFields.map((field) => [field, true])
              )
            };

            settingsMap.set('plantation_table_columns', plantationTableColumns);
          }

          const savedSettings = Array.from(settingsMap.entries()).map(
            ([settings_key, value]) => ({
              settings_key,
              settings_values: value.settings_values
            })
          );

          const plantationTableColumnSettings =
            settingsMap.get('plantation_table_columns')?.settings_values || {};

          this.setState({
            settings: settingsMap,
            savedSettings: {
              dataset: this.crd,
              settings: savedSettings
            },
            plantationTableColumnSettings: plantationTableColumnSettings
          });
        },
        error: () => this.setState({ settings: new Map() })
      });
  }

  setPlantationTableSettings(columnSettings: PlantationTableColumnsSettings) {
    this.setState({ plantationTableColumnSettings: columnSettings });
  }

  getCountries() {
    this.plantationListFilters$
      .pipe(
        map((filters) => filters?.['country']),
        filter((countries) => countries !== undefined),
        map((countries) =>
          countries
            .filter(
              (country) =>
                country.label?.toLowerCase() !==
                GeoJsonTypesEnum.INVALID_GEOMETRY
            )
            .map((country) => country.label)
            .filter((label): label is string => typeof label === 'string')
        )
      )
      .subscribe((res) => this.setState({ countries: res }));
  }

  /**
   * Risk mitigation methods
   */
  getRiskDataByPlantationCode(plantationCodes: string[]) {
    return this.riskMitigationService
      .getRiskDataByPlantationCode(
        plantationCodes,
        this.getValue('selectedPeriod') as string
      )
      .subscribe((res) => this.setState({ riskDataByPlantationCode: res }));
  }

  getAgriTracePlantationsFiles(plantation_doc_id: string) {
    this.setState({ agriTraceFiles: undefined });
    return this.riskMitigationService
      .getAgriTracePlantationsFiles(plantation_doc_id)
      .subscribe({
        next: (res) => this.setState({ agriTraceFiles: res }),
        error: () => this.setState({ agriTraceFiles: [] })
      });
  }

  getAgriTraceFilePreview(payload: PreviewUrlPayload) {
    this.setState({ agriTraceFilePreview: undefined });
    return this.riskMitigationService
      .getAgriTraceFilePreview(payload)
      .subscribe({
        next: (res) =>
          this.setState({
            agriTraceFilePreview: {
              file_url: res?.results?.file_url,
              status: res?.status
            }
          }),
        error: () =>
          this.setState({
            agriTraceFilePreview: {
              file_url: '',
              status: AGRITRACE_PREVIEW_STATUS.ERROR
            }
          })
      });
  }

  /** end risk mitigation methods */
  getSavedSettings() {
    return this.getValue('savedSettings');
  }

  saveSettings(settings: SettingsPayload) {
    this.setState({ savedSettings: settings });
  }

  clearSettings() {
    this.setState({ settings: undefined });
  }

  clearRiskDateByPlantationCode() {
    this.setState({ riskDataByPlantationCode: undefined });
  }

  setPlantationUploadFilename(filename: string) {
    this.setState({ plantationUploadFileName: filename });
  }

  updateDataset(isSiLinkingPage = false) {
    this.eventStateService.datasetChanged = true;
    this.eventStateService.plantationFiltersPayload = null;
    this.resetDashboardStates();
    this.clearSettings();
    this.getSettings();
    this.getPlantationListFilters();
    this.getFavoritePlantationFilters();
    if (!isSiLinkingPage) {
      this.getTotalSummary();
    }
  }

  private fetchAndEnrichPlantations<T extends boolean>(
    params: TableParams,
    isFullResponse: T,
    selectedPeriod: string = INITIAL_SELECTED_PERIOD,
    riskProperties: any[] = [],
    context?: HttpContext
  ) {
    return this.posthogService
      .getFeatureFlag(POSTHOG_KEYS.SUPPLIER_NAME_COLUMN)
      .pipe(
        take(1),
        concatMap((supplierName) =>
          this.dashboardService.getPlantationsPaginated({
            tableParams: {
              ...params,
              orderBy:
                supplierName || !isSupplierNameSort(params.orderBy)
                  ? params.orderBy
                  : undefined,
              period: selectedPeriod
            },
            riskProperties,
            supplierName,
            context
          })
        ),
        map((plantations) => {
          plantations.results.forEach((plantation: any) => {
            riskProperties?.forEach((prop) => {
              const riskBoolean = `${prop.name}_value`;
              if (plantation[riskBoolean]) {
                plantation.risks?.push(prop);
              }
            });
          });

          return (
            isFullResponse ? plantations : plantations.results
          ) as T extends true ? PagedResponse<Plantation> : Plantation[];
        })
      );
  }
}

function isSupplierNameSort(orderBy: string | undefined): boolean {
  return orderBy?.replace(/^-/, '') === SUPPLIER_NAME_COLUMN_FIELD;
}
